import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import HelpDocLink, { HELP_DOC_URLS } from './HelpDocLink';
import { Spinner, Tabs, Tab, Button, ButtonGroup } from 'react-bootstrap';
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from 'react-bootstrap/Form';
import WorkflowDiagram from './WorkflowDiagram';

import { DownloadAllColumnsButton, DownloadMultiHeaderButton } from './biblio/topic_entity_tag/TopicEntityTable.js';

import { setDateRangeDict, setDateOptionDict, setDateFrequencyDict } from '../actions/reportsActions';
import { fetchMLModelsIfNeeded } from '../actions/mlModelsActions';
import ModelsTable from './reports/ModelsTable';
import QCReportDateSelector from './reports/QCReportDateSelector';

import { api } from "../api";
import { convertDate } from '../utils/reportDates';
import { AgGridReact } from 'ag-grid-react';
import { handleGridCopy } from '../utils/gridCopyHandler';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

import DateRangePicker from '@wojtekmaj/react-daterange-picker'
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';

const file_upload_name_mapping = {
    'files uploaded': 'uploaded',
    'file needed': 'needed',
    'file unavailable': 'unavailable',
    'file upload in progress': 'in progress'
}

// Placeholder for a QC report that has not loaded, or whose fetch failed. The
// payload key is simply absent, which the render guards read as "no rows".
const emptyQcReport = { 'date-produced': null };

// A QC report endpoint, optionally pinned to one archived run. An empty or unset
// datestamp asks for the latest report, which is what the page showed before a
// date could be chosen.
const qcReportUrl = (endpoint, datestamp) => (
    datestamp ? `${endpoint}?datestamp=${datestamp}` : endpoint
);

// Shown when a QC report fails to load. Deliberately distinct from the "no rows
// for this mod" message: these reports are acted on, so a failed request must
// never read as a clean bill of health for the run the curator selected.
const QCReportLoadError = ({ selectedDate }) => (
    <div style={{ textAlign: 'left', fontWeight: 'bold' }}>
        Could not load this report{selectedDate ? ` for ${convertDate(selectedDate)}` : ''}.
        Please try again, or pick another date.
    </div>
);

const WorkflowStatTableCounters = ({ workflowProcessAtpId, title, tagNames, nameMapping, modSection, columnType }) => {
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [key, setKey] = useState(0);
  const [countResync, setCountResync] = useState(0);
  const [dateRangeHeaders, setDateRangeHeaders] = useState([]);
  const [dateRangeData, setDateRangeData] = useState({});

  const mods = useSelector(state => state.app.mods);
  const dateRangeDict = useSelector(state => state.reports.dateRangeDict);
  const dateRangeValue = ( (dateRangeDict[modSection]) && (dateRangeDict[modSection][workflowProcessAtpId]) ) ? dateRangeDict[modSection][workflowProcessAtpId] : '';

  const dateOptionDict = useSelector(state => state.reports.dateOptionDict);
  const dateOptionValue = ( (dateOptionDict[modSection]) && (dateOptionDict[modSection][workflowProcessAtpId]) ) ? dateOptionDict[modSection][workflowProcessAtpId] : '';
  const dateFrequencyDict = useSelector(state => state.reports.dateFrequencyDict);
  const dateFrequencyValue = ( (dateFrequencyDict[modSection]) && (dateFrequencyDict[modSection][workflowProcessAtpId]) ) ? dateFrequencyDict[modSection][workflowProcessAtpId] : '';
  const gridRef = useRef();

  const mod_abbreviation = (modSection === 'All') ? '' : modSection;
  const date_range_start = (dateRangeValue === '') ? '': dateRangeValue[0];
  const date_range_end = (dateRangeValue === '') ? '': dateRangeValue[1];
  const date_option = (dateOptionValue === '') ? 'default' : dateOptionValue;

  useEffect(() => {
    const fetchData = async () => {
      let url = `/workflow_tag/counters/?workflow_process_atp_id=${workflowProcessAtpId}`;
      if (mod_abbreviation !== '') { url += `&mod_abbreviation=${mod_abbreviation}`; }
      if ( (date_range_start !== '') && (date_range_end !== '') ) {
          url += `&date_option=${date_option}&date_range_start=${date_range_start}&date_range_end=${date_range_end}`; }
      if ( (date_option !== 'default') && ( (date_range_start === '') ||  (date_range_end === '') ) ) { return; }
      if (dateFrequencyValue !== '') { url += `&date_frequency=${dateFrequencyValue}`; }
      setIsLoadingData(true);
      try {
        const result = await api.get(url);
        setData(result.data);

        const totalsObj = {};
        result.data.forEach(item => {
          //if (!totalsObj[item.workflow_tag_name]) {
          //  totalsObj[item.workflow_tag_name] = 0;
          //}
          //totalsObj[item.workflow_tag_name] += item.tag_count;
             //console.log("url:"+url + "\nwprkflowProcessAtpId:" + workflowProcessAtpId + "  tagNames"+ tagNames + " column type:"+ columnType+ " workflow_tag_name:"+item.workflow_tag_name +  " mod_abbreviation:" + item.mod_abbreviation + " number:" + item.tag_count)
          if (item.mod_abbreviation=="All"){
             totalsObj[item.workflow_tag_name] = item.tag_count?.toLocaleString();
          }
        });
        setTotals(totalsObj);

        if (columnType === 'date_range') {
          setDateRangeData(() => {
            const newData = {};
            result.data.forEach(item => {
              if (!newData[item['workflow_tag_id']]) {
                newData[item['workflow_tag_id']] = {};
              }
              newData[item['workflow_tag_id']][item['time_period']] = item['tag_count'];
            });
            return newData;
          });
          setDateRangeHeaders(() => {
            const newHeaders = [];
            result.data.forEach(item => {
              if (!newHeaders.includes(item['time_period'])) {
                newHeaders.push(item['time_period']);
              }
            });
            if (dateFrequencyValue === 'month') {
              return newHeaders.sort((a, b) => {	// sort by date
                const dateA = new Date(a.split(" ").join(" 1,"));
                const dateB = new Date(b.split(" ").join(" 1,"));
                return dateA - dateB;
              });
            }
            return newHeaders.sort();			// sort normally
          });
        }

        setKey(prevKey => prevKey + 1);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [workflowProcessAtpId, dateOptionValue, date_range_start, date_range_end, countResync, dateFrequencyValue]);

  const getTagCountDateRange = (tagName, mod, dateRange) => {
    const item = data.find(d => d.workflow_tag_name === tagName && d.mod_abbreviation === mod && d.time_period === dateRange);
    return item && typeof item.tag_count === 'number' ? item.tag_count.toLocaleString() : "0";
  };

  const getTagCount = (tagName, mod) => {
    const item = data.find(d => d.workflow_tag_name === tagName && d.mod_abbreviation === mod);
    return item && typeof item.tag_count === 'number' ? item.tag_count.toLocaleString() : "0";
  };

  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '15px',
  };

  const boldCellStyle = (params) => {
    if (params.colDef.field === 'tag_name') {
      return { fontWeight: 'bold', textAlign: 'left' };
    }
    return null;
  };

  let defaultColumns = [
    {
      headerName: '',
      field: 'tag_name',
      flex: 1,
      cellStyle: boldCellStyle,
      headerClass: 'wft-bold-header'
    },
    {
      headerName: 'Total',
      field: 'total',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header'
    }
  ];
  let columns = defaultColumns;
  if (columnType === 'all_mods') {
    columns = [
      ...columns,
      ...mods.map(mod => ({
        headerName: mod,
        field: mod,
        flex: 1,
        cellStyle: { textAlign: 'left' },
        headerClass: 'wft-bold-header'
      })),
    ];
  }
  else if (columnType === 'date_range') {
    columns = [
      ...columns,
      ...dateRangeHeaders.map(dateRangeHeader => ({
        headerName: dateRangeHeader,
        field: dateRangeHeader,
        flex: 1,
        cellStyle: { textAlign: 'left' },
        headerClass: 'wft-bold-header'
      })),
    ];
  }

  let rowData = [];
  if (columnType === 'all_mods') {
    rowData = tagNames.map(tagName => {
      const row = {
        tag_name: nameMapping[tagName],
        total: totals[tagName] || 0
      };
      mods.forEach(mod => {
        row[mod] = getTagCount(tagName, mod);
      });
      return row;
    });
  }
  else if (columnType === 'two_column') {
    rowData = tagNames.map(tagName => {
      const row = {
        tag_name: nameMapping[tagName],
        total: getTagCount(tagName, modSection)
      };
      return row;
    });
  }
  else if (columnType === 'date_range') {
    rowData = tagNames.map(tagName => {
      let total = 0;
      const row = {
        tag_name: nameMapping[tagName],
      };
      dateRangeHeaders.forEach(dateRange => {
        const item = data.find(d => d.workflow_tag_name === tagName && d.mod_abbreviation === modSection && d.time_period === dateRange);
        if (item && typeof item.tag_count === 'number') {
          total += item.tag_count;
          row[dateRange] = item.tag_count.toLocaleString(); }
        else {
          row[dateRange] = "0"; }
      });
      row['total'] = total;
      return row;
    });
  }

  const gridOptions = { autoSizeStrategy: { type: 'fitCellContents', } }
  const fileNameFront = `${modSection}_${title}`.replace(/ /g,"_");

  return (
    <div>
      <h5>{title}</h5>
        <div style={containerStyle}>
          <Container fluid style={{ width: '90%' }}>
            <Row>
              <Col>
                <ReportsDatePicker facetName={workflowProcessAtpId} dateOptionValue={dateOptionValue} dateRangeValue={dateRangeValue} setValueFunction={setDateRangeDict} workflowProcessAtpId={workflowProcessAtpId} modSection={modSection} columnType={columnType} dateFrequencyValue={dateFrequencyValue} />
              </Col>
              <Col style={{ textAlign: "right" }}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setCountResync(countResync + 1) }
                  style={{ marginRight: '5px', width: '120px' }}
                >
                  <FontAwesomeIcon icon={faSync} /> Resync Table
                </Button>
                <DownloadAllColumnsButton
                  gridRef={gridRef}
                  colDefs={columns}
                  rowData={rowData}
                  fileNameFront={fileNameFront}
                  buttonLabel="Download" />
              </Col>
            </Row>
            <Row>
              <Col>
                {isLoadingData ? (
                  <div className="text-center">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : (
                  <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '100%' }}>
                    <AgGridReact
                      key={key}
                      ref={gridRef}
                      rowData={rowData}
                      columnDefs={columns}
                      pagination={false}
                      paginationPageSize={20}
                      enableCellTextSelection={true}
                      ensureDomOrder={true}
                      suppressColumnVirtualisation={true}
                      domLayout="autoHeight"
                      gridOptions = {gridOptions}
                    /><br/><br/><br/>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </div>
    </div>
  );
}; // const const WorkflowStatTableCounters = ({ workflowProcessAtpId, title, tagNames, nameMapping, modSection, columnType })

const ReportsDatePicker = ({ facetName, dateOptionValue, dateRangeValue, setValueFunction, workflowProcessAtpId, modSection, columnType, dateFrequencyValue }) => {
    const dispatch = useDispatch();

    function formatDateRange(dateRange){
            let dateStart=dateRange[0].getFullYear()+"-"+parseInt(dateRange[0].getMonth()+1).toString().padStart(2,'0')+"-"+dateRange[0].getDate().toString().padStart(2,'0');
            let dateEnd=dateRange[1].getFullYear()+"-"+parseInt(dateRange[1].getMonth()+1).toString().padStart(2,'0')+"-"+dateRange[1].getDate().toString().padStart(2,'0');
            return [dateStart,dateEnd];
    }

    function formatToUTCString(dateRange) {
        if (dateRange !== '') {
            let dateStart = new Date (dateRange[0]);
            let offset = dateStart.getTimezoneOffset();
            let parsedDateStart = new Date(Date.parse(dateRange[0]) + (offset * 60000));
            let parsedDateEnd = new Date(Date.parse(dateRange[1]) + (offset * 60000));
            return [parsedDateStart, parsedDateEnd];
        } else {
            return "";
        }
    }

    function handleFixedTimeClick(timeframe) {
        var today = new Date();
        let newDate = ['',''];
        if (timeframe === 'Day') {
            newDate = formatDateRange([today,today]);
        }
        else if (timeframe === 'Week') {
            let lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            newDate = formatDateRange([lastWeek,today]);
        }
        else if (timeframe === 'Month') {
            let lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            newDate = formatDateRange([lastMonth,today]);
        }
        else if (timeframe === 'Year') {
            let lastYear = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
            newDate = formatDateRange([lastYear,today]);
        }
        dispatch(setValueFunction(newDate, workflowProcessAtpId, modSection));
    }

    function handleDateRangeChange(newDateRangeArr){
        if (newDateRangeArr === null) {
            dispatch(setValueFunction('', workflowProcessAtpId, modSection));
        }
        else if(!isNaN(Date.parse(newDateRangeArr[0])) && !isNaN(Date.parse(newDateRangeArr[1]))){
            dispatch(setValueFunction(formatDateRange(newDateRangeArr), workflowProcessAtpId, modSection));
        }
    }

    function handleDateOptionChange(newDateOption){
        dispatch(setDateOptionDict(newDateOption, workflowProcessAtpId, modSection));
    }

    function handleDateFrequencyChange(newDateFrequency){
        dispatch(setDateFrequencyDict(newDateFrequency, workflowProcessAtpId, modSection));
    }

    const dateOptions = { 'Date Workflow Updated': 'default', 'Date added to ABC': 'reference_created', 'Date Published': 'reference_published', 'Date Inside Corpus': 'inside_corpus' };
    const dateFrequencies = { 'year': 'year', 'month': 'month', 'week': 'week' };


    return(
      <div key={facetName} style={{ display: 'flex', alignItems: 'center', textAlign: "left", paddingLeft: "2em", paddingBottom: "0.5em" }}>
        <Form.Control as='select' id='dateOption' name='dateOption' style={{ width: "13em", marginRight: "3em" }} value={dateOptionValue} onChange={(e) => handleDateOptionChange(e.target.value)} >
          {Object.entries(dateOptions).map(([label, value], index) => ( <option value={value} key={index}>{label}</option> ))}
        </Form.Control>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ButtonGroup aria-label="DateSetter" size ="sm" style={{display: "block"}}>
            <Button variant="secondary" style={{'borderBottomLeftRadius' : 0}} onClick= { () => { handleFixedTimeClick('Day') } } >Day</Button>
            <Button variant="secondary" onClick={ () => { handleFixedTimeClick('Week') } } >Week</Button>
            <Button variant="secondary" onClick={ () => { handleFixedTimeClick('Month') } } >Month</Button>
            <Button variant="secondary" style={{'borderBottomRightRadius' : 0}} onClick= { () => { handleFixedTimeClick('Year') } } >Year</Button>
          </ButtonGroup>
          <DateRangePicker value={formatToUTCString(dateRangeValue)} onChange= { (newDateRangeArr) => { handleDateRangeChange(newDateRangeArr) } } />
        </div>
          {(() => {
            if (columnType === 'date_range') { return(
              <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: "2em", paddingBottom: "0.5em" }}>
                Report Frequency  <Form.Control as='select' id='dateFrequency' name='dateFrequency' style={{ width: "13em", marginRight: "3em" }} value={dateFrequencyValue} onChange={(e) => handleDateFrequencyChange(e.target.value)} >
                  {Object.entries(dateFrequencies).map(([label, value], index) => ( <option value={value} key={index}>{label}</option> ))}
                </Form.Control></div>); }
            return null
          })()}
      </div>
    )
} // const ReportsDatePicker = ({ facetName, dateRangeValue, setValueFunction, workflowProcessAtpId, modSection, columnType, dateFrequencyValue })


const WorkflowStatModTablesContainer = ({modSection}) => {
  return (
    <div>
      <h3 style={{ marginBottom: '30px' }}>Workflow Statistics <HelpDocLink url={HELP_DOC_URLS.workflowStatistics} title="Open the Workflow Statistics help documentation" /></h3>
                  <WorkflowStatTableCounters
                    workflowProcessAtpId="ATP:0000140"
                    title="File Upload Current Status"
                    tagNames={Object.keys(file_upload_name_mapping)}
                    nameMapping={file_upload_name_mapping}
                    modSection={modSection}
                    columnType="date_range"
                  />

            {(() => {
              if (modSection === 'SGD') {
                const manual_indexing_name_mapping = {
                    'manual indexing needed': 'needed',
                    'manual indexing complete': 'complete',
                    'manual indexing in progress': 'in progress'
                }
                return (
                  <WorkflowStatTableCounters
                    workflowProcessAtpId="ATP:0000273"
                    title="Reference Manually Indexing"
                    tagNames={Object.keys(manual_indexing_name_mapping)}
                    nameMapping={manual_indexing_name_mapping}
                    modSection={modSection}
                    columnType="two_column"
                  />
                  ) }
              return null
            })()}

      <WorkflowStatModTable
        workflowProcessAtpId="ATP:0000165"
        title="Reference Classification Status"
        modSection={modSection}
      />

      <WorkflowStatModTable
        workflowProcessAtpId="ATP:0000172"
        title="Entity Extraction Status"
        modSection={modSection}
      />

      <WorkflowStatModTable
        workflowProcessAtpId="ATP:0000311"
        title="Curation Classification Status"
        modSection={modSection}
      />
    </div>
  );
};

const WorkflowStatModTable = ({ workflowProcessAtpId, title, modSection }) => {
  const [data, setData] = useState([]);
  const [header, setHeader] = useState([]);
  //const [totals, setTotals] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [key, setKey] = useState(100);
  const [countResync, setCountResync] = useState(0);

  const mods = useSelector(state => state.app.mods);
  const dateRangeDict = useSelector(state => state.reports.dateRangeDict);
  const dateRangeValue = ( (dateRangeDict[modSection]) && (dateRangeDict[modSection][workflowProcessAtpId]) ) ? dateRangeDict[modSection][workflowProcessAtpId] : '';

  const dateOptionDict = useSelector(state => state.reports.dateOptionDict);
  const dateOptionValue = ( (dateOptionDict[modSection]) && (dateOptionDict[modSection][workflowProcessAtpId]) ) ? dateOptionDict[modSection][workflowProcessAtpId] : '';
  const gridRef = useRef();

  const mod_abbreviation = (modSection === 'All') ? '' : modSection;
  const date_range_start = (dateRangeValue === '') ? '': dateRangeValue[0];
  const date_range_end = (dateRangeValue === '') ? '': dateRangeValue[1];
  const date_option = (dateOptionValue === '') ? 'default' : dateOptionValue;

  useEffect(() => {
    const fetchData = async () => {
        console.log("mods is " + mods);
        console.log("mod abbr " + mod_abbreviation);
      let url = `/workflow_tag/reports/${workflowProcessAtpId}/${mod_abbreviation}`;
      // if (mod_abbreviation !== '') { url += `&mod_abbreviation=${mod_abbreviation}`; }
      //if ( (date_range_start !== '') && (date_range_end !== '') ) {
      //    url += `&date_option=${date_option}&date_range_start=${date_range_start}&date_range_end=${date_range_end}`; }
      //if ( (date_option !== 'default') && ( (date_range_start === '') ||  (date_range_end === '') ) ) { return; }
      setIsLoadingData(true);
      try {
        const result = await api.get(url);
        setData(result.data[0]);
        setHeader(result.data[1])

        setKey(prevKey => prevKey + 1);
      } catch (error) {
        console.error('wfsMt: Error fetching data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [workflowProcessAtpId, dateOptionValue, date_range_start, date_range_end, countResync]);


  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '15px',
  };

  const columns = [
      ...header.map(mod => ({
      headerName: mod,
            children: [
                {headerName: '#',
                 cellStyle: { textAlign: 'left' },
                 field: mod + '_num'},
                {headerName: '%',
                 cellStyle: { textAlign: 'left' },
                 field: mod + '_perc'}
            ],
      field: mod,
      flex: 1,
      headerClass: 'wft-bold-header'
    }))
  ];
  columns[0] = {headerName: '', field: 'status', cellStyle: { fontWeight: 'bold', textAlign: 'left' }}

  const gridOptions = { autoSizeStrategy: { type: 'fitCellContents', } }
  const fileNameFront = `${modSection}_${title}`.replace(/ /g,"_");

  return (
    <div>
      <h5>{title}</h5>
        <div style={containerStyle}>
          <Container fluid style={{ width: '90%' }}>
            <Row style={{ paddingBottom: "0.5em" }}>
              <Col style={{ textAlign: "right" }}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setCountResync(countResync + 1) }
                  style={{ marginRight: '5px', width: '120px' }}
                >
                  <FontAwesomeIcon icon={faSync} /> Resync Table
                </Button>
                <DownloadMultiHeaderButton
                  gridRef={gridRef}
                  colDefs={columns}
                  rowData={data}
                  fileNameFront={fileNameFront}
                  buttonLabel="Download" />
              </Col>
            </Row>
            <Row>
              <Col>
                {isLoadingData ? (
                  <div className="text-center">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : (
                  <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '100%' }}>
                    <AgGridReact
                      key={key}
                      ref={gridRef}
                      rowData={data}
                      pagination={false}
                      columnDefs={columns}
                      paginationPageSize={20}
                      enableCellTextSelection={true}
                      ensureDomOrder={true}
                      suppressColumnVirtualisation={true}
                      domLayout="autoHeight"
                      gridOptions = {gridOptions}
                    /><br/><br/><br/>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </div>
    </div>
  );
}; // const WorkflowStatModTable = ({ workflowProcessAtpId, title, modSection })

const WorkflowStatTablesContainer = ({modSection}) => {
  const text_conversion_name_mapping = {
      'file converted to text': 'converted',
      'text conversion needed': 'needed',
      'file to text conversion failed': 'failed',
      'text conversion in progress': 'in progress'
  }
  return (
    <div>
      <h3 style={{ marginBottom: '30px' }}>Workflow Statistics <HelpDocLink url={HELP_DOC_URLS.workflowStatistics} title="Open the Workflow Statistics help documentation" /></h3>
      <WorkflowStatTableCounters
        workflowProcessAtpId="ATP:0000140"
        title="File Upload Current Status"
        tagNames={Object.keys(file_upload_name_mapping)}
	nameMapping={file_upload_name_mapping}
        modSection={modSection}
        columnType="all_mods"
      />
      <WorkflowStatTableCounters
        workflowProcessAtpId="ATP:0000161"
        title="Text Conversion Current Status"
        tagNames={Object.keys(text_conversion_name_mapping)}
        nameMapping={text_conversion_name_mapping}
        modSection={modSection}
        columnType="all_mods"
      />
    </div>
  );
};

const QCReportObsoleteEntities = ({modSection}) => {
  // Kept in component state rather than redux: the report now varies by the
  // selected run, so a store-level "fetched once" cache would pin the first
  // date loaded and never refetch.
  const [qcReportObsoleteEntities, setQcReportObsoleteEntities] = useState(emptyQcReport);
  // null until the date list resolves; '' once it resolves with no history.
  const [selectedDate, setSelectedDate] = useState(null);

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [key, setKey] = useState(0);
  const gridRef = useRef();
  const gridOptions = { autoSizeStrategy: { type: 'fitCellContents', } }

  const paginationPageSizeSelector = useMemo(() => { return [10, 25, 50, 100, 500]; }, []);

  useEffect(() => {
    if (selectedDate === null) { return; }
    let cancelled = false;

    const fetchData = async () => {
      const url = qcReportUrl('/check/check_obsolete_entities', selectedDate);
      setIsLoadingData(true);
      try {
        const result = await api.get(url);
        if (cancelled) { return; }
        setQcReportObsoleteEntities(result.data);
        setLoadFailed(false);
        setKey(prevKey => prevKey + 1);
      } catch (error) {
        if (cancelled) { return; }
        console.error('Error fetching data:', error);
        setQcReportObsoleteEntities(emptyQcReport);
        setLoadFailed(true);
      } finally {
        if (!cancelled) { setIsLoadingData(false); }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [selectedDate]);

  const columnDefs = [
    { headerName: "Entity Type", field: "entity_type", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' },
    { headerName: "Entity Status", field: "entity_status", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' },
    { headerName: "Entity Curie", field: "entity_curie", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' },
    { headerName: "Species", field: "species", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' },
    { headerName: "Entity Name", field: "entity_name", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' },
    { headerName: "Reference Count", field: "reference_count", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' },
    { headerName: "Reference Curies", field: "reference_curies", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header',
      cellRenderer: (params) => {
        const values = params.value?.split(',').map(s => s.trim()).filter(Boolean);
        if (!values?.length) return null;
        return (
          <span>
            {values.map((val, index) => {
              const isAGRKB = val.startsWith("AGRKB:");
              return (
                <span key={index}>
                  {isAGRKB ? (
                    <a href={`${process.env.REACT_APP_UI_URL}/Biblio?action=display&referenceCurie=${val}`} target="_blank" rel="noopener noreferrer" >{val}</a>
                  ) : (
                    val
                  )}
                  {index < values.length - 1 ? ', ' : ''}
                </span>
              );
            })}
          </span>
        );
      }
    }
  ];

  let rowData = [];

  if ( qcReportObsoleteEntities["obsolete_entities"] && ( modSection in qcReportObsoleteEntities["obsolete_entities"] ) ) {
    rowData = qcReportObsoleteEntities["obsolete_entities"][modSection].map(item => ({
      entity_type: item.entity_type,
      entity_status: item.entity_status,
      entity_curie: item.entity_curie,
      species: item.species || "",
      entity_name: item.entity_name || "N/A",
      reference_count: item.reference_count,
      reference_curies: item.reference_curies
    }));
  }

  return (
    <div>
      <div>
        <Container fluid style={{ width: '90%' }}>
          <Row>
            <Col>
              <h4 style={{ textAlign: 'left' }}>TET Table Deleted or Obsoleted Entities <HelpDocLink url={HELP_DOC_URLS.qcObsoleteEntities} title="Open the help documentation for this report" /></h4>
            </Col>
          </Row>
          <Row>
            <Col>
              <QCReportDateSelector
                reportKey="obsolete_entities"
                instanceId={modSection}
                selectedDate={selectedDate}
                onChange={setSelectedDate}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {isLoadingData ? (
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '100%' }}>
                 {loadFailed ? (<QCReportLoadError selectedDate={selectedDate} />) : (<>
                 {( qcReportObsoleteEntities['date-produced'] !== null) &&
                  (<div style={{ textAlign: 'left' }}>Date Produced: {convertDate(qcReportObsoleteEntities['date-produced'])}<br /><br /></div>) }
                 {( qcReportObsoleteEntities["obsolete_entities"] && ( modSection in qcReportObsoleteEntities["obsolete_entities"] ) ) ? (
                  <AgGridReact
                    key={key}
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    suppressColumnVirtualisation={true}
                    domLayout="autoHeight"
                    gridOptions = {gridOptions}
                  />
                  ) : (<div style={{ textAlign: 'left', fontWeight: 'bold' }}> No obsolete or deleted entities for {modSection}</div>)}
                 </>)}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}; // const QCReportObsoleteEntities

const QCReportRetractedPapers = ({modSection}) => {
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const gridOptions = { autoSizeStrategy: { type: 'fitCellContents', } }
    const paginationPageSizeSelector = useMemo(() => { return [10, 25, 50, 100, 500]; }, []);
    // Component state, not redux: see the note in QCReportObsoleteEntities.
    const [qcReportRedactedPapers, setQcReportRedactedPapers] = useState(emptyQcReport);
    const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
        if (selectedDate === null) { return; }
        let cancelled = false;

        const fetchData = async () => {
            const url = qcReportUrl('/check/check_redacted_references_with_tags', selectedDate);
            setIsLoadingData(true);
            try {
                const result = await api.get(url);
                if (cancelled) { return; }
                setQcReportRedactedPapers(result.data);
                setLoadFailed(false);
            } catch (error) {
                if (cancelled) { return; }
                console.error('Error fetching data:', error);
                setQcReportRedactedPapers(emptyQcReport);
                setLoadFailed(true);
            } finally {
                if (!cancelled) { setIsLoadingData(false); }
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [selectedDate]);

    const columnDefs = [
        { headerName: "Reference ID",
            field: "reference_id",
            cellRenderer: props => {
                return <a href={process.env.REACT_APP_UI_URL+"/Biblio?action=display&referenceCurie="+props.value}>{props.value}</a>
            },
            flex:1, cellStyle: { textAlign: 'left' },
            headerClass: 'wft-bold-header' },
        {
            headerName: "Reference Status",
            field: "reference_status",
            flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' }
    ];

    let rowData = [];

    if ( qcReportRedactedPapers["redacted-references"] && ( modSection in qcReportRedactedPapers["redacted-references"] ) ) {
        rowData = qcReportRedactedPapers["redacted-references"][modSection].map(item => ({
            reference_id: item.reference_id,
            reference_status: 'Retracted'
        }));
    }


    return (
        <div>
            <Container fluid style={{ width: '90%' }}>
                <Row>
                    <Col>
                        <h4 style={{ textAlign: 'left' }}>Retracted References with Manual Tags <HelpDocLink url={HELP_DOC_URLS.qcRetractedRefs} title="Open the help documentation for this report" /></h4>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <QCReportDateSelector
                            reportKey="redacted_references"
                instanceId={modSection}
                            selectedDate={selectedDate}
                            onChange={setSelectedDate}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {isLoadingData ? (
                            <div className="text-center">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                            </div>
                        ) : (
                            <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '100%' }}>
                                {loadFailed ? (<QCReportLoadError selectedDate={selectedDate} />) : (<>
                                {( qcReportRedactedPapers['date-produced'] !== null) &&
                                    (<div style={{ textAlign: 'left' }}>Date Produced: {convertDate(qcReportRedactedPapers['date-produced'])}<br /><br /></div>) }
                                {( qcReportRedactedPapers["redacted-references"] && ( modSection in qcReportRedactedPapers["redacted-references"] ) ) ? (
                                    <AgGridReact
                                        rowData={rowData}
                                        columnDefs={columnDefs}
                                        pagination={true}
                                        paginationPageSize={10}
                                        paginationPageSizeSelector={paginationPageSizeSelector}
                                        enableCellTextSelection={true}
                                        ensureDomOrder={true}
                                        suppressColumnVirtualisation={true}
                                        domLayout="autoHeight"
                                        gridOptions = {gridOptions}
                                    />
                                ) : (<div style={{ textAlign: 'left', fontWeight: 'bold' }}> No retracted papers with manual tags for {modSection}</div>)}
                                </>)}
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

const QCObsoletePmids = ({ modSection }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (selectedDate === null) { return; }
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await api.get(qcReportUrl('/check/check_obsolete_pmids', selectedDate));
        if (cancelled) { return; }
        setData(result.data);
        setLoadFailed(false);
      } catch (error) {
        if (cancelled) { return; }
        console.error('Error fetching obsolete PMIDs:', error);
        setData(null);
        setLoadFailed(true);
      } finally {
        if (!cancelled) { setIsLoading(false); }
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [selectedDate]);

  const produced = data?.['date-produced'];
  const pmids = data?.obsolete_pmids?.[modSection] || [];

  return (
    <Container fluid style={{ width: '90%' }}>
      <Row style={{ paddingTop: '1em', paddingBottom: '0.5em' }}>
        <Col>
          <h4 style={{ textAlign: 'left' }}>PMIDs obsoleted this month <HelpDocLink url={HELP_DOC_URLS.qcObsoletePmids} title="Open the help documentation for this report" /></h4>
        </Col>
      </Row>

      <Row>
        <Col>
          <QCReportDateSelector
            reportKey="obsolete_pmids"
                instanceId={modSection}
            selectedDate={selectedDate}
            onChange={setSelectedDate}
          />
        </Col>
      </Row>

      <Row>
        <Col style={{ textAlign: 'left' }}>
          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : loadFailed ? (
            <QCReportLoadError selectedDate={selectedDate} />
          ) : (
            <>
              {produced && (
                <div style={{ textAlign: 'left' }}>
                  Date Produced: {convertDate(produced)}
                  <br /><br />
                </div>
              )}

              {pmids.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '1.5em', textAlign: 'left' }}>
                  {pmids.map(pmid => (
                    <li key={pmid}>{pmid}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ textAlign: 'left', fontWeight: 'bold' }}>
                  No obsolete PMIDs this month for {modSection}
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};


const QCReportDuplicateOrcids = ({ modSection }) => {
  // Component state, not redux: see the note in QCReportObsoleteEntities.
  const [qcReportDuplicateOrcids, setQcReportDuplicateOrcids] = useState(emptyQcReport);
  const [selectedDate, setSelectedDate] = useState(null);

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [key, setKey] = useState(0);
  const gridRef = useRef();
  const gridOptions = { autoSizeStrategy: { type: 'fitCellContents', } }

  const paginationPageSizeSelector = useMemo(() => { return [10, 25, 50, 100, 500]; }, []);

  useEffect(() => {
    if (selectedDate === null) { return; }
    let cancelled = false;

    const fetchData = async () => {
      const url = qcReportUrl('/check/check_duplicate_orcids', selectedDate);
      setIsLoadingData(true);
      try {
        const result = await api.get(url);
        if (cancelled) { return; }
        setQcReportDuplicateOrcids(result.data);
        setLoadFailed(false);
        setKey(prevKey => prevKey + 1);
      } catch (error) {
        if (cancelled) { return; }
        console.error('Error fetching data:', error);
        setQcReportDuplicateOrcids(emptyQcReport);
        setLoadFailed(true);
      } finally {
        if (!cancelled) { setIsLoadingData(false); }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [selectedDate]);

    const columnDefs = [
      { headerName: "Reference ID",
          field: "reference_curie",
          cellRenderer: props => {
              return <a href={process.env.REACT_APP_UI_URL+"/Biblio?action=display&referenceCurie="+props.value}>{props.value}</a>
          },
          flex:1, cellStyle: { textAlign: 'left' },
          headerClass: 'wft-bold-header' },
      { headerName: "ORCID", field: "orcid", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' },
      { headerName: "Author Names", field: "author_names", flex:1, cellStyle: { textAlign: 'left' }, headerClass: 'wft-bold-header' }
    ];

  let rowData = [];
  if ( qcReportDuplicateOrcids["duplicate_orcids"] && ( modSection in qcReportDuplicateOrcids["duplicate_orcids"] ) ) {
    rowData = qcReportDuplicateOrcids["duplicate_orcids"][modSection].map(item => ({
      reference_curie: item.reference_curie,
      orcid: item.orcid,
      author_names: item.author_names
    }));
  }

  return (
    <div>
      <div>
        <Container fluid style={{ width: '90%' }}>
          <Row>
            <Col>
              <h4 style={{ textAlign: 'left' }}>Duplicate ORCIDs <HelpDocLink url={HELP_DOC_URLS.qcDuplicateOrcids} title="Open the help documentation for this report" /></h4>
            </Col>
          </Row>
          <Row>
            <Col>
              <QCReportDateSelector
                reportKey="duplicate_orcids"
                instanceId={modSection}
                selectedDate={selectedDate}
                onChange={setSelectedDate}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {isLoadingData ? (
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '100%' }}>
                 {loadFailed ? (<QCReportLoadError selectedDate={selectedDate} />) : (<>
                 {( qcReportDuplicateOrcids['date-produced'] !== null) &&
                  (<div style={{ textAlign: 'left' }}>Date Produced: {convertDate(qcReportDuplicateOrcids['date-produced'])}<br /><br /></div>) }
                 {( qcReportDuplicateOrcids["duplicate_orcids"] && ( modSection in qcReportDuplicateOrcids["duplicate_orcids"] ) ) ? (
                  <AgGridReact
                    key={key}
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    suppressColumnVirtualisation={true}
                    domLayout="autoHeight"
                    gridOptions = {gridOptions}
                  />
                  ) : (<div style={{ textAlign: 'left', fontWeight: 'bold' }}> No duplicate ORCIDs for {modSection}</div>)}
                 </>)}
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}; // const QCReportDuplicateOrcids


const ReportsContainer = () => {
  const dispatch = useDispatch();
  const mods = useSelector(state => state.app.mods);
  const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
  const testerMod = useSelector(state => state.isLogged.testerMod);
  const activeMod = (testerMod !== 'No') ? testerMod : cognitoMod;

  const mlModelsData = useSelector(state => state.mlModels.data);
  useEffect(() => { dispatch(fetchMLModelsIfNeeded()); }, [dispatch]);
  const modsWithModels = useMemo(
    () => new Set((mlModelsData || []).map(r => r.mod_abbreviation).filter(Boolean)),
    [mlModelsData]
  );
  const anyModels = (mlModelsData || []).length > 0;

  return (
    <div>
      <Tabs mountOnEnter="true" defaultActiveKey={activeMod !== 'No' ? activeMod : 'all'} id="mods-tabs">
        <Tab eventKey="all" title="All">
          <Tabs mountOnEnter="true" defaultActiveKey="all_stats" id="all-reports-tabs">
            <Tab eventKey="all_stats" title="Workflow Statistics">
              <WorkflowStatTablesContainer modSection='All' />
            </Tab>
            {anyModels && (
              <Tab eventKey="all_models" title="Models">
                <ModelsTable modSection="All" />
              </Tab>
            )}
          </Tabs>
        </Tab>
        {mods.map(mod => (
          <Tab key={mod} eventKey={mod} title={mod}>
            <Tabs mountOnEnter="true" defaultActiveKey={`${mod}_qcreport`} id={`${mod}_reports-tabs`}>
              <Tab eventKey={`${mod}_qcreport`} title="QC Reports">
                {/* Tab-level heading: the individual report components only carry
                    their own h4 titles. */}
                <h3 style={{ marginBottom: '30px' }}>QC Reports <HelpDocLink url={HELP_DOC_URLS.abcReportsPage} title="Open the ABC Reports Page help documentation" /></h3>
                <QCReportObsoleteEntities modSection={mod} />
                  <hr/>
                <QCReportRetractedPapers modSection={mod} />
                  <hr/>
                <QCObsoletePmids modSection={mod} />
                  <hr/>
                <QCReportDuplicateOrcids modSection={mod} />
              </Tab>
              <Tab eventKey={`${mod}_stats`} title="Workflow Statistics">
                <WorkflowStatModTablesContainer modSection={mod} />
              </Tab>
              {!['MGI', 'RGD', 'XB'].includes(mod) && (
                <Tab eventKey={`${mod}_diagram`} title="Workflow Diagram">
                  <WorkflowDiagram mod={mod} />
                </Tab>
              )}
              {modsWithModels.has(mod) && (
                <Tab eventKey={`${mod}_models`} title="Models">
                  <ModelsTable modSection={mod} />
                </Tab>
              )}
            </Tabs>
          </Tab>
        ))}
      </Tabs>
    </div>
  );
}

export default ReportsContainer;
